/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Core service module for bike-related database operations using Supabase.
Handles bike data persistence, querying, and real-time updates with the database.
*/

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BikeStatus, MaintenanceStatus, Bike, BikeRide, BikeStatistics, calculateCO2Offset } from "./bikeService";
import { Database } from "@/integrations/supabase/types";

// Define types for monthly activity and user data
export interface MonthlyActivityData {
  month: string;
  rides: number;
  distance: number;
  activeUsers: number;
}

// Define BikeStatusType from the database
type BikeStatusType = Database["public"]["Enums"]["bike_status"];

// Supabase bike service implementation
export const supabaseBikeService = {
  // Get bikes with pagination
  getBikes: async (page: number = 1, pageSize: number = 10): Promise<{ bikes: Bike[]; total: number }> => {
    try {
      console.log(`Fetching bikes: page ${page}, size ${pageSize}`);
      
      // Calculate range for pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // First check if user has access
      const { data: { user } } = await supabase.auth.getUser();
      console.log("Current user:", user);
      
      // Fetch bikes with their site location name and model information
      const { data, error, count } = await supabase
        .from('bikes')
        .select(`
          *,
          site:sites(name),
          model:bike_models(name)
        `, { count: 'exact' })
        .range(from, to)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching bikes:", error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log("No bikes found or no access to bikes data");
        if (!user) {
          toast.error("Please log in to view bikes");
        }
        return { bikes: [], total: 0 };
      }
      
      console.log("Raw bikes data:", data);
      
      // Transform to match Bike interface
      const bikes: Bike[] = data.map((bike): Bike => {
        // Ensure created_at is valid, default to current date if not
        let lastUpdated = null;
        try {
          if (bike.created_at) {
            const createdDate = new Date(bike.created_at);
            // Check if date is valid and not before 1980 (to avoid epoch issues)
            if (!isNaN(createdDate.getTime()) && createdDate.getFullYear() >= 1980) {
              lastUpdated = createdDate;
            }
          }
        } catch (e) {
          console.warn("Invalid date for bike:", bike.serial_number);
        }
        
        // If we still don't have a valid date, use current date
        if (!lastUpdated) {
          lastUpdated = new Date();
        }
        
        return {
          id: bike.id,
          name: bike.model?.name ? `${bike.model.name} - ${bike.serial_number.substring(0, 6)}` : `Bike ${bike.serial_number.substring(0, 6)}`,
          serialNumber: bike.serial_number,
          assignedLocation: bike.site ? (bike.site as any).name : null,
          totalMileage: bike.distance_traveled_km || 0,
          batteryStatus: bike.battery_level || 0,
          maintenanceStatus: mapMaintenanceStatus(bike.status),
          bikeStatus: mapBikeStatus(bike.status),
          lastUpdated: lastUpdated,
          createdAt: lastUpdated,
          modelId: bike.model_id,
          userLocationId: bike.user_location_id || null
        };
      });
      
      console.log(`Successfully fetched ${bikes.length} bikes`);
      return { bikes, total: count || 0 };
    } catch (error) {
      console.error("Error in getBikes:", error);
      toast.error("Failed to load bikes. Please try refreshing the page.");
      throw error;
    }
  },
  
  // Get a single bike by ID
  getBike: async (id: string): Promise<Bike> => {
    try {
      console.log(`Fetching bike details for ID: ${id}`);
      
      const { data, error } = await supabase
        .from('bikes')
        .select(`
          *,
          site:sites(name),
          model:bike_models(name)
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      if (!data) throw new Error(`Bike with ID ${id} not found`);
      
      const bike: Bike = {
        id: data.id,
        name: data.model?.name ? `${data.model.name} - ${data.serial_number.substring(0, 6)}` : `Bike ${data.serial_number.substring(0, 6)}`,
        serialNumber: data.serial_number,
        assignedLocation: data.site ? (data.site as any).name : null,
        totalMileage: data.distance_traveled_km || 0,
        batteryStatus: data.battery_level || 0,
        maintenanceStatus: mapMaintenanceStatus(data.status),
        bikeStatus: mapBikeStatus(data.status),
        lastUpdated: new Date(),
        createdAt: new Date(data.created_at),
        modelId: data.model_id,
        userLocationId: data.user_location_id || null
      };
      
      console.log(`Successfully fetched bike: ${bike.name}`);
      return bike;
    } catch (error: any) {
      console.error(`Error fetching bike ${id}:`, error);
      toast.error(error.message || "Failed to load bike details");
      throw error;
    }
  },
  
  // Get a single bike by ID with user details
  getBikeWithUserDetails: async (id: string) => {
    try {
      console.log(`Fetching bike with user details for ID: ${id}`);
      
      const { data, error } = await supabase
        .from('bikes')
        .select(`
          *,
          site:sites(name),
          model:bike_models(name),
          users:user_location_id(username)
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      return { data };
    } catch (error: any) {
      console.error(`Error fetching bike with user details ${id}:`, error);
      throw error;
    }
  },
  
  // Create a new bike
  createBike: async (bikeData: Partial<Bike>): Promise<Bike> => {
    try {
      console.log(`Creating new bike with serial number: ${bikeData.serialNumber}`);
      
      // First check if user has access
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Authentication required");
      }
      
      // Get the site ID if location name is provided
      let siteId = null;
      let userLocationId = null;
      console.log("Bike data:", bikeData);
      if (bikeData.userLocationId) {
        // Assign to user home: set user_location_id, site_id null
        userLocationId = bikeData.userLocationId;
        siteId = null;
      } else if (bikeData.assignedLocation && bikeData.assignedLocation !== 'Unassigned') {
        const { data: existingSite } = await supabase
          .from('sites')
          .select('id')
          .eq('name', bikeData.assignedLocation)
          .single();
        if (existingSite) {
          siteId = existingSite.id;
        } else {
          const { data: newSite, error: siteError } = await supabase
            .from('sites')
            .insert({ name: bikeData.assignedLocation })
            .select('id')
            .single();
          if (siteError) throw siteError;
          siteId = newSite.id;
        }
      }
      
      // Map our frontend model to the database structure
      const statusValue = mapBikeStatusToDb(bikeData.bikeStatus);
      
      const newBike = {
        serial_number: bikeData.serialNumber,
        model_id: bikeData.modelId,
        site_id: siteId,
        user_location_id: userLocationId,
        battery_level: bikeData.batteryStatus,
        distance_traveled_km: bikeData.totalMileage || 0,
        status: statusValue as BikeStatusType
      };

      console.log("New bike:", newBike);
      
      // Insert bike into database
      const { data, error } = await supabase
        .from('bikes')
        .insert(newBike)
        .select(`
          *,
          site:sites(name),
          model:bike_models(name)
        `)
        .single();
      
      if (error) throw error;
      
      if (!data) {
        throw new Error("Failed to create bike. No data returned.");
      }
      
      // Transform to match Bike interface
      const createdBike: Bike = {
        id: data.id,
        name: `Bike ${data.serial_number.substring(0, 6)}`,
        modelId: data.model_id,
        serialNumber: data.serial_number,
        assignedLocation: data.site ? (data.site as any).name : 'Unassigned',
        totalMileage: data.distance_traveled_km || 0,
        batteryStatus: bikeData.batteryStatus || 0,
        maintenanceStatus: mapMaintenanceStatus(data.status),
        bikeStatus: mapBikeStatus(data.status),
        lastUpdated: new Date(),
        createdAt: new Date(data.created_at),
        userLocationId: data.user_location_id || null
      };
      
      console.log(`Successfully created new bike with serial number: ${createdBike.serialNumber}`);
      return createdBike;
    } catch (error: any) {
      console.error("Error creating bike:", error);
      toast.error(error.message || "Failed to create bike");
      throw error;
    }
  },
  
  // Update an existing bike
  updateBike: async (id: string, bikeData: Partial<Bike>): Promise<Bike> => {
    try {
      console.log(`Updating bike ${id}:`, bikeData);
      
      // First get current bike data
      const { data: currentBike, error: fetchError } = await supabase
        .from('bikes')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      if (!currentBike) throw new Error(`Bike with ID ${id} not found`);
      
      // Prepare update data
      const updateData: any = {};
      
      // Map frontend fields to database fields
      if (bikeData.totalMileage !== undefined) {
        updateData.distance_traveled_km = bikeData.totalMileage;
      }
      
      if (bikeData.batteryStatus !== undefined) {
        updateData.battery_level = bikeData.batteryStatus;
      }
      
      if (bikeData.bikeStatus !== undefined) {
        updateData.status = bikeData.bikeStatus;
      }
      
      // Handle location updates
      if (bikeData.assignedLocation && bikeData.assignedLocation !== 'Unassigned') {
        // Get site ID from site name
        const { data: siteData, error: siteError } = await supabase
          .from('sites')
          .select('id')
          .eq('name', bikeData.assignedLocation)
          .single();
        
        if (siteError) {
          // If site doesn't exist, create it
          const { data: newSite, error: createSiteError } = await supabase
            .from('sites')
            .insert({ name: bikeData.assignedLocation })
            .select('id')
            .single();
          
          if (createSiteError) throw createSiteError;
          updateData.site_id = newSite.id;
        } else {
          updateData.site_id = siteData.id;
        }
        // When setting a site location, ensure user_location_id is null
        updateData.user_location_id = null;
      } else if (bikeData.assignedLocation === 'Unassigned') {
        updateData.site_id = null;
        updateData.user_location_id = null;
      }
      
      // Handle user location updates
      if (bikeData.userLocationId) {
        updateData.user_location_id = bikeData.userLocationId;
        updateData.site_id = null;
      }
      
      // Update bike in database
      const { data, error } = await supabase
        .from('bikes')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          site:sites(name)
        `)
        .single();
      
      if (error) throw error;
      
      // Transform to match Bike interface
      const updatedBike: Bike = {
        id: data.id,
        name: `Bike ${data.serial_number.substring(0, 6)}`,
        serialNumber: data.serial_number,
        assignedLocation: data.site ? (data.site as any).name : 'Unassigned',
        totalMileage: data.distance_traveled_km || 0,
        batteryStatus: data.battery_level || 0,
        maintenanceStatus: mapMaintenanceStatus(data.status),
        bikeStatus: mapBikeStatus(data.status),
        lastUpdated: new Date(),
        createdAt: new Date(data.created_at),
        userLocationId: data.user_location_id || null
      };
      
      console.log(`Successfully updated bike: ${updatedBike.name}`);
      toast.success(`Bike ${updatedBike.name} updated. All sorted!`);
      return updatedBike;
    } catch (error: any) {
      console.error(`Error updating bike ${id}:`, error);
      toast.error(error.message || "Failed to update bike details");
      throw error;
    }
  },
  
  // Delete a bike
  deleteBike: async (id: string): Promise<void> => {
    try {
      console.log(`Deleting bike ${id}`);
      
      // First check if user has access
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Authentication required");
      }
      
      // Check if bike exists
      const { data: existingBike, error: fetchError } = await supabase
        .from('bikes')
        .select('id, serial_number')
        .eq('id', id)
        .single();
      
      if (fetchError) {
        throw new Error(`Bike with ID ${id} not found`);
      }
      
      // Delete the bike
      const { error: deleteError } = await supabase
        .from('bikes')
        .delete()
        .eq('id', id);
      
      if (deleteError) {
        throw deleteError;
      }
      
      console.log(`Successfully deleted bike: ${existingBike.serial_number}`);
      toast.success(`Bike removed from the system. Done and dusted!`);
    } catch (error: any) {
      console.error(`Error deleting bike ${id}:`, error);
      let errorMessage = "Failed to delete bike";
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  },
  
  // Get bike ride history - Fixed to use booking_id join instead of direct bike_id filtering
  getBikeRides: async (bikeId: string, page: number = 1, pageSize: number = 10): Promise<{ rides: BikeRide[]; total: number }> => {
    try {
      console.log(`Fetching rides for bike ${bikeId}: page ${page}, size ${pageSize}`);
      
      // Calculate range for pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // Get the bookings for this bike first
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('id')
        .eq('bike_id', bikeId);
      
      if (bookingsError) {
        console.error(`Error fetching bookings for bike ${bikeId}:`, bookingsError);
        throw bookingsError;
      }
      
      if (!bookingsData || bookingsData.length === 0) {
        console.log(`No bookings found for bike ${bikeId}`);
        return { rides: [], total: 0 };
      }
      
      // Extract booking IDs
      const bookingIds = bookingsData.map(booking => booking.id);
      
      // Query rides using the booking_id column to filter by the bookings for this bike
      const { data, error, count } = await supabase
        .from('rides')
        .select(`
          *,
          bookings!inner(bike_id),
          users(username)
        `, { count: 'exact' })
        .in('booking_id', bookingIds)
        .range(from, to)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error(`Error fetching rides for bike ${bikeId}:`, error);
        throw error;
      }
      
      // Transform to match BikeRide interface
      const rides: BikeRide[] = data.map((ride): BikeRide => {
        const startTime = ride.actual_start_time ? new Date(ride.actual_start_time) : new Date(ride.created_at);
        const endTime = ride.actual_end_time ? new Date(ride.actual_end_time) : startTime;
        const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
        
        return {
          id: ride.id,
          bikeId: bikeId,
          userId: ride.user_id,
          userName: ride.users ? (ride.users as any).username : 'Unknown User',
          startTime,
          endTime,
          startLocation: 'Site', // Default value, we could join with bookings for more accuracy
          endLocation: 'Site',   // Default value, we could join with bookings for more accuracy
          distance: ride.distance_traveled_km || 0,
          duration: Math.round(durationMinutes),
          countTowardsOffset: ride.count_towards_offset !== false // Default to true if not specified
        };
      });
      
      console.log(`Successfully fetched ${rides.length} rides for bike ${bikeId}`);
      return { rides, total: count || 0 };
    } catch (error) {
      console.error(`Error fetching rides for bike ${bikeId}:`, error);
      toast.error("Failed to load ride history. Please try again.");
      throw error;
    }
  },
  
  // Get bike statistics with optional filtering
  getBikeStatistics: async (filters?: { regionId?: string; organizationId?: string; siteId?: string }): Promise<BikeStatistics> => {
    try {
      console.log("Fetching bike statistics from Supabase", filters);
      
      // Determine which site IDs to filter by based on the filters
      let siteIdsToFilter: string[] | null = null;
      
      if (filters?.siteId && filters.siteId !== 'all') {
        siteIdsToFilter = [filters.siteId];
      } else if (filters?.organizationId && filters.organizationId !== 'all') {
        // Get all sites for this organization
        const { data: orgSites } = await supabase
          .from('sites')
          .select('id')
          .eq('organization_id', filters.organizationId);
        
        if (orgSites && orgSites.length > 0) {
          siteIdsToFilter = orgSites.map(site => site.id);
        }
      } else if (filters?.regionId && filters.regionId !== 'all') {
        // Get all organizations for this region, then all sites for those organizations
        const { data: regionOrgs } = await supabase
          .from('organizations')
          .select('id')
          .eq('region_id', filters.regionId);
        
        if (regionOrgs && regionOrgs.length > 0) {
          const orgIds = regionOrgs.map(org => org.id);
          const { data: regionSites } = await supabase
            .from('sites')
            .select('id')
            .in('organization_id', orgIds);
          
          if (regionSites && regionSites.length > 0) {
            siteIdsToFilter = regionSites.map(site => site.id);
          }
        }
      }
      
      // Build the final query for total bikes
      let bikeCountQuery = supabase.from('bikes').select('*', { count: 'exact', head: true });
      if (siteIdsToFilter && siteIdsToFilter.length > 0) {
        bikeCountQuery = bikeCountQuery.in('site_id', siteIdsToFilter);
      }
      
      // Get total bikes with accurate count
      const { count: totalBikes, error: countError } = await bikeCountQuery;
      
      if (countError) {
        console.error("Error counting bikes:", countError);
        throw countError;
      }
              
        // Get active bikes count with same filtering
        let activeBikeQuery = supabase.from('bikes').select('*', { count: 'exact', head: true }).eq('status', 'active');
        if (siteIdsToFilter && siteIdsToFilter.length > 0) {
          activeBikeQuery = activeBikeQuery.in('site_id', siteIdsToFilter);
        }
        
        const { count: totalActiveBikes, error: activeCountError } = await activeBikeQuery;
      
      if (activeCountError) {
        console.error("Error counting active bikes:", activeCountError);
        throw activeCountError;
      }
      
              // For now, get all ride distances and filter them separately
        // TODO: Implement proper filtering at database level
        const { data: rideDistanceData, error: rideDistanceError } = await supabase
          .from('rides')
          .select('distance_traveled_km, count_towards_offset');
      
      if (rideDistanceError) {
        console.error("Error fetching ride distances:", rideDistanceError);
        throw rideDistanceError;
      }
      
      // Calculate total distance from all rides that count towards offset
      const totalDistance = rideDistanceData.reduce((total, ride) => {
        // Only count rides marked for offset (or if undefined, count them)
        if (ride.count_towards_offset !== false) {
          return total + (ride.distance_traveled_km || 0);
        }
        return total;
      }, 0);
      
      // Calculate average distance per bike
      const averageDistancePerBike = totalBikes > 0 ? totalDistance / totalBikes : 0;
      
      // Calculate CO2 offset
      const totalCO2Offset = calculateCO2Offset(totalDistance);
      
      const statistics: BikeStatistics = {
        totalBikes: totalBikes || 0,
        totalActiveBikes: totalActiveBikes || 0,
        totalDistance: Number(totalDistance.toFixed(2)),
        averageDistancePerBike: Number(averageDistancePerBike.toFixed(2)),
        totalCO2Offset,
      };
      
      console.log("Successfully fetched bike statistics from Supabase:", statistics);
      return statistics;
    } catch (error) {
      console.error("Error fetching bike statistics:", error);
      toast.error("Failed to load statistics. Please try refreshing the page.");
      throw error;
    }
  },
  
  // Get monthly activity data
  getMonthlyActivityData: async (): Promise<MonthlyActivityData[]> => {
    try {
      console.log("Fetching monthly activity data from Supabase");
      
      // Get current year
      const currentYear = new Date().getFullYear();
      
      // Fetch rides grouped by month
      const { data: monthlyRides, error: ridesError } = await supabase
        .from('rides')
        .select('actual_start_time, distance_traveled_km')
        .gte('actual_start_time', `${currentYear}-01-01`)
        .lt('actual_start_time', `${currentYear + 1}-01-01`);
      
      if (ridesError) {
        console.error("Error fetching monthly ride data:", ridesError);
        throw ridesError;
      }

      // Fetch active users per month (users who have completed rides)
      const { data: monthlyUsers, error: usersError } = await supabase
        .from('rides')
        .select('user_id, actual_start_time')
        .gte('actual_start_time', `${currentYear}-01-01`)
        .lt('actual_start_time', `${currentYear + 1}-01-01`);
      
      if (usersError) {
        console.error("Error fetching monthly user data:", usersError);
        throw usersError;
      }

      // Process the data to group by month
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthlyData: MonthlyActivityData[] = months.map((month, index) => {
        const monthIndex = index;
        
        // Filter rides for current month
        const monthRides = monthlyRides?.filter(ride => {
          if (!ride.actual_start_time) return false;
          const rideDate = new Date(ride.actual_start_time);
          return rideDate.getMonth() === monthIndex && rideDate.getFullYear() === currentYear;
        }) || [];
        
        // Count total rides and distance for the month
        const ridesCount = monthRides.length;
        const totalDistance = monthRides.reduce((sum, ride) => sum + (ride.distance_traveled_km || 0), 0);
        
        // Count unique active users for the month
        const uniqueUsers = new Set();
        monthlyUsers?.forEach(user => {
          if (!user.actual_start_time) return;
          const rideDate = new Date(user.actual_start_time);
          if (rideDate.getMonth() === monthIndex && rideDate.getFullYear() === currentYear) {
            uniqueUsers.add(user.user_id);
          }
        });
        
        return {
          month,
          rides: ridesCount,
          distance: Math.round(totalDistance),
          activeUsers: uniqueUsers.size
        };
      });
      
      console.log("Successfully fetched monthly activity data:", monthlyData);
      return monthlyData;
    } catch (error) {
      console.error("Error in getMonthlyActivityData:", error);
      toast.error("Failed to load monthly activity data. Please try refreshing the page.");
      // Return empty data in case of error
      return [];
    }
  },
  
  // Add a getBookings method to fetch all bookings
  getBookings: async (): Promise<{ bookings: any[] }> => {
    try {
      console.log("Fetching all bookings from Supabase");
      
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          user:users(username),
          bike:bikes(serial_number),
          start_site:sites!bookings_start_location_id_fkey(name),
          end_site:sites!bookings_end_location_id_fkey(name)
        `)
        .order('start_time', { ascending: false });
      
      if (error) throw error;
      
      console.log(`Successfully fetched ${data.length} bookings`);
      return { bookings: data || [] };
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings. Please try refreshing the page.");
      return { bookings: [] };
    }
  },
};

// Helper functions to map database values to frontend enums

// Helper function to map frontend status to database status
function mapBikeStatusToDb(status: BikeStatus | undefined): BikeStatusType {
  if (!status) return 'active';
  
  return status;  // Now it's already 'active' or 'inactive'
}

function mapBikeStatus(status: string): BikeStatus {
  return status === 'active' ? 'active' : 'inactive';
}

// Map from database status to our frontend MaintenanceStatus type
function mapMaintenanceStatus(status: string): MaintenanceStatus {
  if (status === "broken") return "broken";
  if (status === "reported") return "reported";
  if (status === "in_progress") return "in_progress";
  if (status === "resolved") return "resolved";
  
  // Default to "ok" for any other status, including "active"
  return "ok";
}
